import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { ShortcutItem } from '@/Interfaces/ShortcutItem.interface';
import { applyDefaultTimes } from '@/helpers/datePicker/applyDefaultTime';
import { ArrowDropDown, CalendarMonthOutlined } from '@mui/icons-material';
import { inputBaseClasses } from '@mui/material';
import { LocalizationProvider, PickerChangeHandlerContext, pickersLayoutClasses } from '@mui/x-date-pickers';
import {
  DateRange,
  DateRangePicker,
  DateRangePickerProps,
  DateRangeValidationError,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { DayRangeValidationProps } from '@mui/x-date-pickers-pro/internals/models';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import ActionBar from './ActionBar';
import ActionBarWithTimeHandling from './ActionBarWithTimeHandling';
import Shortcuts from './Shortcuts';

interface CustomShortcutContext extends Omit<PickerChangeHandlerContext<DateRangeValidationError>, 'shortcut'> {
  shortcut?: ShortcutItem;
}

interface IProps extends Omit<DateRangePickerProps<Moment>, 'onChange'> {
  dateRangeLengthLimit?: number;
  dateRangeFormat?: FilterDate;
  shortcuts?: ShortcutItem[];
  onChange: (value: DateRange<Moment>, format?: FilterDate) => void;
  inputProps?: Record<string, any>;
  fullWidth?: boolean;
  disabled?: boolean;
  withTime?: boolean;
}

const DateRangeWithShortcuts = forwardRef<HTMLDivElement, IProps>(
  (
    {
      minDate,
      dateRangeLengthLimit,
      value = [null, null],
      dateRangeFormat = FilterDate.CUSTOM_RANGE,
      shortcuts = [],
      onChange,
      inputProps,
      fullWidth = true,
      closeOnSelect = false,
      disabled = false,
      withTime = false,
      ...props
    }: IProps,
    ref,
  ) => {
    const [pendingValue, setPendingValue] = useState<DateRange<Moment>>(value);
    const [shortcut, setShortcut] = useState<FilterDate>(dateRangeFormat);
    const [open, setOpen] = useState<boolean>(false);
    const [isNestedPickerOpen, setIsNestedPickerOpen] = useState(false);

    const onOpen = useCallback(() => setOpen(true), []);
    const onClose = useCallback(() => {
      if (!isNestedPickerOpen) {
        setOpen(false);
      }
    }, [isNestedPickerOpen]);

    const handleChange = useCallback(
      (newValue: DateRange<Moment>, ctx: CustomShortcutContext) => {
        const [newStart, newEnd] = newValue;

        let start = newStart;
        let end = newEnd;

        if (dateRangeLengthLimit && start && end) {
          if ((end.diff(start, 'days') ?? 0) + 1 > dateRangeLengthLimit) {
            end = start.clone().add({ days: dateRangeLengthLimit - 1 });
          }
        }

        const withTimes = withTime ? applyDefaultTimes([start, end], pendingValue) : [start, end];
        setPendingValue(withTimes as DateRange<Moment>);
        setShortcut(ctx?.shortcut?.value || FilterDate.CUSTOM_RANGE);
      },
      [dateRangeLengthLimit, pendingValue, withTime],
    );

    const setShortcutToCustom = useCallback(() => {
      setShortcut(FilterDate.CUSTOM_RANGE);
    }, []);

    const handleAccept = useCallback(() => {
      const finalizedValue: DateRange<Moment> = withTime
        ? pendingValue
        : [
            pendingValue[0] ? pendingValue[0].clone().startOf('day') : null,
            pendingValue[1] ? pendingValue[1].clone().endOf('day') : null,
          ];

      onChange(finalizedValue, shortcut);
      setOpen(false);
    }, [onChange, pendingValue, shortcut, withTime]);

    const shouldDisableDate = useMemo<DayRangeValidationProps<Moment>['shouldDisableDate']>(() => {
      if (!dateRangeLengthLimit) {
        return undefined;
      }
      return (date, position) => {
        if (position === 'start' || !pendingValue[0]) {
          return false;
        }

        const start = pendingValue[0];
        const d1 = moment([start.year(), start.month(), start.date()]);
        const d2 = moment([date.year(), date.month(), date.date()]);

        const diff = d2.diff(d1, 'days') ?? 0;

        return diff + 1 > dateRangeLengthLimit;
      };
    }, [pendingValue, dateRangeLengthLimit]);

    const _minDate = useMemo(() => {
      if (!minDate) return moment(0);
      return moment(minDate).startOf('day');
    }, [minDate]);

    useEffect(() => {
      if (open) return;
      setPendingValue([value[0] ? moment(value[0]) : null, value[1] ? moment(value[1]) : null]);
    }, [value, open]);

    useEffect(() => {
      if (open) return;
      setShortcut(dateRangeFormat);
    }, [dateRangeFormat]);

    return (
      <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
        <DateRangePicker
          ref={ref}
          {...props}
          closeOnSelect={closeOnSelect}
          minDate={_minDate}
          format={DateFormat.NumericMonthDayYear}
          showDaysOutsideCurrentMonth
          open={open}
          onOpen={onOpen}
          onClose={onClose}
          shouldDisableDate={shouldDisableDate}
          disabled={disabled}
          slots={{
            field: SingleInputDateRangeField,
            actionBar: withTime ? ActionBarWithTimeHandling : ActionBar,
            // @ts-expect-error prop overrides
            shortcuts: Shortcuts,
          }}
          slotProps={{
            textField: {
              fullWidth,
              InputProps: {
                startAdornment: <CalendarMonthOutlined />,
                endAdornment: <ArrowDropDown />,
                sx: {
                  [`& .${inputBaseClasses.input}`]: {
                    padding: '12px 16px 12px 8px',
                  },
                  ...inputProps?.InputProps?.sx,
                },
                ...inputProps?.InputProps,
              },
            },
            shortcuts: {
              items: shortcuts,
              changeImportance: 'set',
              // @ts-expect-error items prop override
              shortcutValue: shortcut,
              setShortcutToCustom,
            },
            actionBar: {
              // @ts-expect-error items prop override
              value: pendingValue,
              onAccept: handleAccept,
              onRangeChange: (next: DateRange<Moment>) => {
                setPendingValue(next);
              },
              onNestedPickerOpenChange: setIsNestedPickerOpen,
            },
            layout: {
              sx: {
                [`.${pickersLayoutClasses.actionBar}`]: {
                  gridColumn: '2/4',
                  gridRow: 3,
                },
                [`.${pickersLayoutClasses.shortcuts}`]: {
                  gridColumn: 1,
                  gridRow: '1/4',
                },
              },
            },
          }}
          value={pendingValue}
          // @ts-expect-error custom shortcut logic
          onChange={handleChange}
          onAccept={handleAccept}
        />
      </LocalizationProvider>
    );
  },
);

export default DateRangeWithShortcuts;
